import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent as PopoverContentOriginal,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon,
  Filter,
  Loader2,
  ClipboardList,
  Eye,
  MapPin,
  Flag,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { useSiteTexts } from "@/cms/useSiteText";
import BookingDetailsDialog from "../components/profile/BookingDetailsDialog";
import { useAuth } from "../Authcontext";
import { getUserBookings, updateBookingStatus } from "@/services/bookingService";

function toJsDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function bookingDate(booking) {
  return (
    booking.startDate ||
    booking.roundDates?.[0] ||
    booking.checkInDate ||
    booking.check_in_date ||
    booking.booking_date
  );
}

function bookingTypeMatches(booking, type) {
  if (type === "all") return true;
  if (type === "golf") return Boolean(booking.selectedCourses?.length);
  if (type === "ferry") return Boolean(booking.selectedFerry);
  if (type === "transport") return Boolean(booking.selectedTransport);
  if (type === "accommodation") return Boolean(booking.selectedAccommodation);
  return false;
}

async function fetchAllUserBookings(userId) {
  const bookings = [];
  let cursor = null;
  let hasMore = true;

  while (hasMore) {
    const page = await getUserBookings(userId, { pageSize: 100, cursor });
    bookings.push(...page.bookings);
    cursor = page.nextCursor;
    hasMore = page.hasMore;
  }

  return bookings;
}

const PopoverContent =
  /** @type {React.ComponentType<React.PropsWithChildren<{ className?: string; align?: string; sideOffset?: number }>>} */ (
    PopoverContentOriginal
  );

export default function MyBookings() {
  const { user, loading: authLoading } = useAuth();
  const t = useSiteTexts({
    title: "myBookings.title",
    filtersHeading: "myBookings.filtersHeading",
    allDatesPlaceholder: "myBookings.allDatesPlaceholder",
    clearFiltersButton: "myBookings.clearFiltersButton",
    emptyTitle: "myBookings.emptyTitle",
    emptySubtext: "myBookings.emptySubtext",
    cancelConfirm: "myBookings.cancelConfirm",
  });
  const [bookingType, setBookingType] = useState("all");
  const [filterDate, setFilterDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedBooking, setSelectedBooking] = useState(null);

  const queryClient = useQueryClient();

  const {
    data: bookings = [],
    isLoading: bookingsLoading,
    isError: isBookingsError,
    error: bookingsError,
  } = useQuery({
    queryKey: ["myBookings", user?.uid],
    queryFn: () => fetchAllUserBookings(user.uid),
    enabled: !authLoading && Boolean(user?.uid),
  });

  // ── Mutations ────────────────────────────────────────────────────────────
  const cancelBookingMutation = useMutation({
    mutationFn: (booking) => updateBookingStatus(booking.id, "cancelled"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["myBookings", user?.uid] }),
  });

  // ── Combine & filter ─────────────────────────────────────────────────────
  const filteredBookings = bookings
    .filter((booking) => {
      const status =
        booking.status === "pending_payment" ? "pending" : booking.status;
      const typeMatch = bookingTypeMatches(booking, bookingType);
      const statusMatch = statusFilter === "all" || status === statusFilter;
      const date = toJsDate(bookingDate(booking));
      const dateMatch =
        !filterDate ||
        (date && format(date, "yyyy-MM-dd") === format(filterDate, "yyyy-MM-dd"));
      return typeMatch && statusMatch && dateMatch;
    })
    .sort((a, b) => {
      const aCreated = toJsDate(a.createdAtServer || a.createdAt)?.getTime() || 0;
      const bCreated = toJsDate(b.createdAtServer || b.createdAt)?.getTime() || 0;
      return bCreated - aCreated;
    });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleCancelBooking = async (booking) => {
    if (window.confirm(t.cancelConfirm)) {
      await cancelBookingMutation.mutateAsync(booking);
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
  };

  const clearFilters = () => {
    setBookingType("all");
    setFilterDate(null);
    setStatusFilter("all");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Identity bar (matches Profile page) ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-[#0B2421] flex items-center justify-center ring-2 ring-[#D4AF37] ring-offset-2 shrink-0">
              <ClipboardList className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-widest mb-0.5">Your reservations</p>
              <h1 className="text-[#0B2421] font-bold text-2xl leading-tight">{t.title}</h1>
              <p className="text-gray-400 text-sm">View and manage all your reservations</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">
        {/* Filters card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-5">
            <Filter className="h-4 w-4 text-[#0B2421]" />
            <h2 className="text-base font-bold text-[#0B2421]">{t.filtersHeading}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Booking Type</label>
              <Select value={bookingType} onValueChange={setBookingType}>
                <SelectTrigger className="border-gray-200 text-gray-700 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="golf">Golf</SelectItem>
                  <SelectItem value="ferry">Ferry</SelectItem>
                  <SelectItem value="transport">Land Transport</SelectItem>
                  <SelectItem value="accommodation">Accommodation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="border-gray-200 text-gray-700 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="w-full flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 hover:border-[#0B2421] transition-colors bg-white">
                    <CalendarIcon className="h-4 w-4 text-[#D4AF37]" />
                    {filterDate ? format(filterDate, "dd MMM yyyy") : t.allDatesPlaceholder}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={filterDate || undefined} onSelect={setFilterDate} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-end">
              <button type="button" onClick={clearFilters}
                className="w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors">
                {t.clearFiltersButton}
              </button>
            </div>
          </div>
        </motion.div>

        {cancelBookingMutation.isError && (
          <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 mb-4">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{cancelBookingMutation.error?.message || "Failed to cancel this booking."}</span>
          </div>
        )}

        {/* Bookings list */}
        {authLoading || bookingsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#0B2421]" />
          </div>
        ) : !user ? (
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-center text-gray-600">
            Sign in to view your bookings.
          </div>
        ) : isBookingsError ? (
          <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{bookingsError?.message || "Failed to load your bookings."}</span>
          </div>
        ) : filteredBookings.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.2 }}
            className="text-center py-20">
            <div className="w-16 h-16 bg-[#0B2421]/8 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="h-8 w-8 text-[#0B2421]/40" />
            </div>
            <h3 className="text-xl font-bold text-[#0B2421] mb-2">{t.emptyTitle}</h3>
            <p className="text-gray-400 text-sm">{t.emptySubtext}</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking, index) => {
              const primaryCourse = booking.selectedCourses?.[0];
              const rounds = booking.selectedPackage?.rounds ?? booking.selectedCourses?.length ?? 0;
              const title =
                primaryCourse?.name ||
                booking.selectedPackage?.name ||
                booking.selectedAccommodation?.title ||
                "Golf Getaway";
              const destination =
                [primaryCourse?.city, primaryCourse?.country].filter(Boolean).join(", ") ||
                booking.selectedAccommodation?.destination;
              const date = toJsDate(bookingDate(booking));
              const status =
                booking.status === "pending_payment" ? "pending" : booking.status;

              return (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                  className="rounded-2xl border border-gray-200 bg-white p-5 flex flex-col sm:flex-row items-start justify-between gap-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 shrink-0 rounded-full border-2 border-[#0B2421]/20 flex items-center justify-center bg-[#0B2421]/6">
                        <Flag className="h-5 w-5 text-[#0B2421]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-[#0B2421] truncate">{title}</h3>
                        {destination && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{destination}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div>
                        <div className="text-gray-400 text-xs">Date</div>
                        <div className="font-semibold text-[#0B2421] text-sm">
                          {date ? format(date, "dd MMM yyyy") : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Rounds</div>
                        <div className="font-semibold text-[#0B2421] text-sm">{rounds}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Players</div>
                        <div className="font-semibold text-[#0B2421] text-sm">{booking.numPlayers || 1}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Total</div>
                        <div className="font-bold text-[#D4AF37] text-lg">
                          SGD {Number(booking.grandTotal || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold capitalize text-gray-700">
                        {status || "unknown"}
                      </span>
                      <span className="text-xs text-gray-400">
                        Ref: {booking.reservationNumber || booking.id}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => handleViewDetails(booking)}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0B2421] hover:bg-[#0B2421]/85 text-white"
                    >
                      <Eye className="h-3.5 w-3.5" /> View Details
                    </button>
                    {status === "confirmed" && (
                      <button
                        type="button"
                        onClick={() => handleCancelBooking(booking)}
                        disabled={cancelBookingMutation.isPending}
                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <BookingDetailsDialog
          open={Boolean(selectedBooking)}
          onOpenChange={(open) => {
            if (!open) setSelectedBooking(null);
          }}
          booking={selectedBooking}
        />
      </div>
    </div>
  );
}
